import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { Download, Copy, Check, FileImage, FileCode2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Reusable QR code dialog that renders the QR **client-side** via
 * `qrcode.react`. Adds download PNG / download SVG and copy URL actions.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (v: boolean) => void
 *  - url: string — the URL encoded inside the QR
 *  - fileName: string — base file name for downloads (without extension)
 *  - title: string (optional)
 */
export default function QRCodeDialog({
  open,
  onOpenChange,
  url,
  fileName,
  title,
}) {
  const { t } = useTranslation();
  const canvasWrapRef = useRef(null);
  const svgWrapRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const safeName =
    (fileName || "qrise-qrcode")
      .replace(/[^a-z0-9-_]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase() || "qrise-qrcode";

  function triggerDownload(href, name) {
    const a = document.createElement("a");
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function downloadPng() {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    triggerDownload(dataUrl, `${safeName}.png`);
    toast.success(t("qr.download_success"));
  }

  function downloadSvg() {
    const svg = svgWrapRef.current?.querySelector("svg");
    if (!svg) return;
    const clone = svg.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const xml = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    triggerDownload(href, `${safeName}.svg`);
    setTimeout(() => URL.revokeObjectURL(href), 1000);
    toast.success(t("qr.download_success"));
  }

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(url || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error(t("qr.copy_error"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="qr-dialog">
        <DialogHeader>
          <DialogTitle>{title || t("qr.title")}</DialogTitle>
          <DialogDescription>{t("qr.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-5 pt-2 pb-1">
          <div
            className="relative rounded-2xl p-5 bg-white shadow-[0_20px_60px_-20px_rgba(139,92,246,0.35)] border border-border"
            data-testid="qr-visual"
          >
            {/* Visible SVG (scales cleanly) */}
            <div ref={svgWrapRef} className="block">
              {url ? (
                <QRCodeSVG
                  value={url}
                  size={220}
                  level="H"
                  marginSize={2}
                  fgColor="#0f172a"
                  bgColor="#ffffff"
                />
              ) : null}
            </div>
            {/* Hidden canvas used for PNG export (higher resolution) */}
            <div ref={canvasWrapRef} className="hidden">
              {url ? (
                <QRCodeCanvas
                  value={url}
                  size={1024}
                  level="H"
                  marginSize={2}
                  fgColor="#0f172a"
                  bgColor="#ffffff"
                />
              ) : null}
            </div>
          </div>

          <div
            className="w-full rounded-xl border border-border bg-muted/60 px-3 py-2 text-xs font-mono text-muted-foreground break-all text-center"
            data-testid="qr-url-display"
          >
            {url}
          </div>

          <div className="grid grid-cols-3 gap-2 w-full">
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-xl text-xs"
              onClick={copyUrl}
              data-testid="qr-copy-btn"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 mr-1.5" />
              ) : (
                <Copy className="h-3.5 w-3.5 mr-1.5" />
              )}
              {copied ? t("qr.copied") : t("qr.copy_url")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-xl text-xs"
              onClick={downloadSvg}
              data-testid="qr-download-svg-btn"
            >
              <FileCode2 className="h-3.5 w-3.5 mr-1.5" />
              SVG
            </Button>
            <Button
              type="button"
              className="h-9 rounded-xl text-xs bg-foreground text-background hover:bg-foreground/90"
              onClick={downloadPng}
              data-testid="qr-download-png-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              PNG
            </Button>
          </div>

          <div className="flex items-center gap-2 text-[11px] text-muted-foreground pt-1">
            <FileImage className="h-3 w-3" />
            {t("qr.print_hint")}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
